import React from 'react';
import { ScrollView, View } from 'react-native';

import tw from '@/styles/tailwind';
import Text from '@/components/Text';

// ───────────────────────────────────────────────────────────────────────────
// Reporte "Pagos semanales" (concentrado anual de pagos, por comunidad).
// Es el ledger plano de TODOS los pagos/egresos del año, una fila por pago,
// ordenado por semana. Misma fuente que las tablas de Egresos del Informe
// mensual (Accounting\Expense), pero de todo el año y SIN partir por cuenta:
// aquí "Cuenta" es una columna más.
//
// Mapeo backend (aún sin endpoint):
//  • Monto/Descripción/Proveedor/Fechas/Cuenta → del Expense
//    (proveedor = expense.paymentRequest?.provider?.name ?? expense.provider_name).
//  • Semana / Mes → derivados de la fecha de pago (ISO week / mes).
//  • Folio        → consecutivo (request_id); las canceladas conservan su folio.
//  • Elaboró      → usuario que registró el pago (expense.created_by).
//  • Pasivo       → saldo pendiente del egreso (pago parcial).
//  • Complemento  → complemento de pago CFDI.
//  • Cancelada    → estado del pago (conserva folio, monto en blanco).
// TODO(backend): reemplazar DEMO por useQuery(communityId, año).
// ───────────────────────────────────────────────────────────────────────────

type Cuenta = 'Mantenimiento' | 'Fondo de Reserva';

type Pago = {
  semana: number;
  folio: number;
  elaboro: string;
  descripcion: string;
  monto: number | null;        // null si cancelada
  pasivo: number | null;       // saldo pendiente
  complemento: number | null;  // complemento de pago CFDI
  fechaSolicitud: string;
  fechaPago: string | null;
  proveedor: string;
  cuenta: Cuenta;
  cancelada?: boolean;
};

const COMMUNITY = 'Comunidad Real Bilbao A.C';
const ANIO = '2026';

// ─── Datos DEMO (concentrado 2026, semanas 2–12) ─────────────────────────────
const PAGOS: Pago[] = [
  { semana: 2,  folio: 1,  elaboro: 'URSB', descripcion: 'Servicio de contabilidad fiscal enero 2026', monto: 5628.0, pasivo: null, complemento: null, fechaSolicitud: '06/01/2026', fechaPago: '08/01/2026', proveedor: 'Asesores Aguirre FJG', cuenta: 'Mantenimiento' },
  { semana: 2,  folio: 2,  elaboro: 'URSB', descripcion: 'Reembolso de internet caseta de vigilancia diciembre 2025', monto: 489.0, pasivo: null, complemento: null, fechaSolicitud: '06/01/2026', fechaPago: '09/01/2026', proveedor: 'Uriel Levi Sánchez Barraza', cuenta: 'Mantenimiento' },
  { semana: 3,  folio: 3,  elaboro: 'URSB', descripcion: 'Checador digital para prestadores de servicio del fraccionamiento', monto: 4906.8, pasivo: null, complemento: null, fechaSolicitud: '13/01/2026', fechaPago: '16/01/2026', proveedor: 'Mantenimiento y Venta de Equipo de Radiocomunicación', cuenta: 'Mantenimiento' },
  { semana: 3,  folio: 4,  elaboro: 'URSB', descripcion: 'Servicio de internet de caseta enero 2026', monto: 489.0, pasivo: null, complemento: null, fechaSolicitud: '13/01/2026', fechaPago: '16/01/2026', proveedor: 'Telmex Teléfonos de México S.A.B de C.V', cuenta: 'Mantenimiento' },
  { semana: 3,  folio: 5,  elaboro: 'URSB', descripcion: 'Traspaso de cuenta de mantenimiento a cuenta de fondo de reserva', monto: 1800.0, pasivo: null, complemento: null, fechaSolicitud: '13/01/2026', fechaPago: '16/01/2026', proveedor: 'Comunidad Real Bilbao A.C', cuenta: 'Mantenimiento' },
  { semana: 4,  folio: 6,  elaboro: 'URSB', descripcion: 'Dos mil recibos de cobro', monto: 5393.25, pasivo: null, complemento: null, fechaSolicitud: '19/01/2026', fechaPago: '22/01/2026', proveedor: 'Edmundo Munguía Gheno', cuenta: 'Mantenimiento' },
  { semana: 4,  folio: 7,  elaboro: 'URSB', descripcion: 'Suministro de agua pipa tipo rabón', monto: 1740.0, pasivo: null, complemento: null, fechaSolicitud: '19/01/2026', fechaPago: '22/01/2026', proveedor: 'Marybon Construcciones S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 3,  folio: 8,  elaboro: 'URSB', descripcion: 'Traspaso de cuenta fondo de reserva a cuenta de mantenimiento', monto: 1200.0, pasivo: null, complemento: null, fechaSolicitud: '13/01/2026', fechaPago: '16/01/2026', proveedor: 'Comunidad Real Bilbao A.C', cuenta: 'Fondo de Reserva' },
  { semana: 5,  folio: 10, elaboro: 'URSB', descripcion: 'CANCELADA', monto: null, pasivo: null, complemento: null, fechaSolicitud: '28/01/2026', fechaPago: null, proveedor: 'Grupo CC. PYJ S.A de C.V', cuenta: 'Mantenimiento', cancelada: true },
  { semana: 6,  folio: 11, elaboro: 'URSB', descripcion: 'CANCELADA', monto: null, pasivo: null, complemento: null, fechaSolicitud: '03/02/2026', fechaPago: null, proveedor: 'Grupo CC. PYJ S.A de C.V', cuenta: 'Mantenimiento', cancelada: true },
  { semana: 7,  folio: 12, elaboro: 'URSB', descripcion: 'Sistema de riego por aspersión en parque principal Real Bilbao', monto: 179593.0, pasivo: null, complemento: null, fechaSolicitud: '09/02/2026', fechaPago: '13/02/2026', proveedor: 'Grupo CC. PYJ S.A de CV', cuenta: 'Mantenimiento' },
  { semana: 7,  folio: 13, elaboro: 'URSB', descripcion: 'Servicio de internet febrero 2026', monto: 489.0, pasivo: null, complemento: null, fechaSolicitud: '09/02/2026', fechaPago: '13/02/2026', proveedor: 'Telmex Teléfonos de México S.A.B de C.V', cuenta: 'Mantenimiento' },
  { semana: 8,  folio: 14, elaboro: 'URSB', descripcion: 'Servicio de contabilidad fiscal febrero 2026', monto: 5628.0, pasivo: null, complemento: null, fechaSolicitud: '16/02/2026', fechaPago: '19/02/2026', proveedor: 'Asesores Aguirre FJG', cuenta: 'Mantenimiento' },
  { semana: 8,  folio: 15, elaboro: 'URSB', descripcion: 'Traspaso por pagos de cuotas de mantenimiento depositadas en el fondo', monto: 12990.0, pasivo: null, complemento: null, fechaSolicitud: '16/02/2026', fechaPago: '19/02/2026', proveedor: 'Comunidad Real Bilbao A.C', cuenta: 'Fondo de Reserva' },
  { semana: 9,  folio: 16, elaboro: 'URSB', descripcion: 'Servicio de administración mes de enero 2026', monto: 59044.0, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 17, elaboro: 'URSB', descripcion: 'Servicio de mantenimiento conjunto, privadas y calles enero 2026', monto: 106452.74, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 18, elaboro: 'URSB', descripcion: 'Servicio de vigilancia mes de enero 2026', monto: 107764.0, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 19, elaboro: 'URSB', descripcion: 'Servicio de administración mes de febrero 2026', monto: 59044.0, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 20, elaboro: 'URSB', descripcion: 'Servicio de recolección de basura mes de febrero 2026', monto: 7500.0, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 21, elaboro: 'URSB', descripcion: 'Devolución de suministros de material para pintura de ventanas de la torre', monto: 1183.5, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Uriel Levi Sánchez Barraza', cuenta: 'Mantenimiento' },
  { semana: 9,  folio: 22, elaboro: 'URSB', descripcion: 'Servicio de recolección de basura mes de enero 2026', monto: 7500.0, pasivo: null, complemento: null, fechaSolicitud: '24/02/2026', fechaPago: '27/02/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 10, folio: 24, elaboro: 'URSB', descripcion: '100 tags RFID para lectoras de largo alcance + 100 llaveros de proximidad 125khz', monto: 5591.2, pasivo: null, complemento: null, fechaSolicitud: '03/03/2026', fechaPago: '05/03/2026', proveedor: 'Tío Ninja Solutions', cuenta: 'Mantenimiento' },
  { semana: 11, folio: 25, elaboro: 'URSB', descripcion: 'Servicio de vigilancia 24x24 horas del 1 al 28 de febrero', monto: 107764.0, pasivo: null, complemento: null, fechaSolicitud: '10/03/2026', fechaPago: '13/03/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 11, folio: 26, elaboro: 'URSB', descripcion: 'Mantenimiento del fraccionamiento febrero 2026', monto: 106452.74, pasivo: null, complemento: null, fechaSolicitud: '10/03/2026', fechaPago: '13/03/2026', proveedor: 'Comunidad Integral RR S.A de C.V', cuenta: 'Mantenimiento' },
  { semana: 11, folio: 27, elaboro: 'URSB', descripcion: 'Servicio de internet marzo 2026', monto: 599.0, pasivo: null, complemento: null, fechaSolicitud: '10/03/2026', fechaPago: '13/03/2026', proveedor: 'Telmex Teléfonos de México S.A.B de C.V', cuenta: 'Mantenimiento' },
  { semana: 11, folio: 28, elaboro: 'URSB', descripcion: 'Traspaso de cuenta de fondo de reserva a cuenta de mantenimiento', monto: 600.0, pasivo: null, complemento: null, fechaSolicitud: '10/03/2026', fechaPago: '13/03/2026', proveedor: 'Comunidad Real Bilbao A.C', cuenta: 'Fondo de Reserva' },
  { semana: 12, folio: 29, elaboro: 'URSB', descripcion: 'Servicio contable mes de marzo 2026', monto: 5628.0, pasivo: null, complemento: null, fechaSolicitud: '17/03/2026', fechaPago: null, proveedor: 'Asesores Aguirre FJG', cuenta: 'Mantenimiento' },
  { semana: 12, folio: 30, elaboro: 'URSB', descripcion: 'Servicio aplicación Ding-Dong marzo 2026', monto: 4823.33, pasivo: null, complemento: null, fechaSolicitud: '17/03/2026', fechaPago: null, proveedor: 'Tío Ninja Solutions', cuenta: 'Mantenimiento' },
];

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Mes derivado de la fecha de pago (o de solicitud si aún no se paga). Formato DD/MM/YYYY.
const mesFromFecha = (f?: string | null) => {
  if (!f) return '';
  const mm = parseInt(f.split('/')[1] ?? '0', 10);
  return MESES[mm - 1] ?? '';
};

const money = (n: number | null) =>
  n == null ? '—' : n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

type Row = Pago & { mes: string };
type Align = 'left' | 'right' | 'center';
type Col = {
  label: string;
  w: number;
  align?: Align;
  get: (r: Row) => string;
  numericKey?: 'monto' | 'pasivo' | 'complemento'; // suma en totales
};

const COLS: Col[] = [
  { label: 'Sem.', w: 50, align: 'center', get: r => String(r.semana) },
  { label: 'Mes', w: 84, get: r => r.mes },
  { label: 'Folio', w: 56, align: 'center', get: r => String(r.folio) },
  { label: 'Elaboró', w: 72, get: r => r.elaboro },
  { label: 'Descripción', w: 300, align: 'left', get: r => r.descripcion },
  { label: 'Monto', w: 116, align: 'right', get: r => money(r.monto), numericKey: 'monto' },
  { label: 'Pasivo', w: 90, align: 'right', get: r => money(r.pasivo), numericKey: 'pasivo' },
  { label: 'Complemento', w: 112, align: 'right', get: r => money(r.complemento), numericKey: 'complemento' },
  { label: 'F. Solicitud', w: 104, get: r => r.fechaSolicitud },
  { label: 'F. Pago', w: 100, get: r => r.fechaPago ?? '—' },
  { label: 'Proveedor', w: 210, align: 'left', get: r => r.proveedor },
  { label: 'Cuenta', w: 130, get: r => r.cuenta },
];

const alignStyle = (a?: Align) => (a === 'right' ? 'items-end' : a === 'center' ? 'items-center' : 'items-start');

export default function PagosSemanales({ communityId, date }: { communityId?: string; date?: Date }) {
  const rows: Row[] = PAGOS.map(p => ({ ...p, mes: mesFromFecha(p.fechaPago ?? p.fechaSolicitud) }));

  const totals = {
    monto: rows.reduce((a, r) => a + (r.monto ?? 0), 0),
    pasivo: rows.reduce((a, r) => a + (r.pasivo ?? 0), 0),
    complemento: rows.reduce((a, r) => a + (r.complemento ?? 0), 0),
  };

  return (
    <View style={tw`px-4 pb-10`}>
      <View style={tw`row justify-between items-center mb-4`}>
        <View>
          <Text style={tw`text-lg font-700 text-secondary`}>Pagos semanales {ANIO}</Text>
          <Text style={tw`text-gray-400 text-xs`}>{COMMUNITY}</Text>
        </View>
        <Text style={tw`text-gray-400`}>{rows.length} pagos</Text>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator style={tw`bg-white rounded-xl border border-gray-100`}>
        <View>
          {/* Header */}
          <View style={tw`row border-b border-gray-300 bg-secondary/10`}>
            {COLS.map(col => (
              <View key={col.label} style={tw.style(`px-2 py-2 border-r border-gray-200`, { width: col.w }, alignStyle(col.align))}>
                <Text style={tw`text-secondary font-600 text-xs`} numberOfLines={2}>{col.label}</Text>
              </View>
            ))}
          </View>

          {/* Filas */}
          {rows.map((r, idx) => (
            <View
              key={r.folio}
              style={tw.style(`row`, r.cancelada ? `bg-amber-100` : idx % 2 === 1 && `bg-gray-50/40`)}
            >
              {COLS.map(col => {
                const isDescCancel = r.cancelada && col.label === 'Descripción';
                return (
                  <View key={col.label} style={tw.style(`px-2 py-2 border-r border-b border-gray-100 justify-center`, { width: col.w }, alignStyle(col.align))}>
                    <Text
                      style={tw.style(`text-xs`, isDescCancel ? `text-red-600 font-700` : `text-gray-700`)}
                      numberOfLines={2}
                    >
                      {col.get(r)}
                    </Text>
                  </View>
                );
              })}
            </View>
          ))}

          {/* Totales */}
          <View style={tw`row border-t-2 border-secondary/40 bg-gray-50`}>
            {COLS.map((col, i) => {
              if (i === 0) {
                // span sobre Sem..Elaboró..Descripción hasta antes de Monto
                const spanW = COLS.slice(0, 5).reduce((a, c) => a + c.w, 0);
                return (
                  <View key={col.label} style={tw.style(`px-2 py-2 justify-center`, { width: spanW })}>
                    <Text style={tw`text-secondary font-700 text-xs`}>TOTALES</Text>
                  </View>
                );
              }
              if (i < 5) return null; // cubiertos por el span
              return (
                <View key={col.label} style={tw.style(`px-2 py-2 border-r border-gray-100 justify-center`, { width: col.w }, alignStyle(col.align))}>
                  <Text style={tw`text-gray-900 font-700 text-xs`}>
                    {col.numericKey ? money(totals[col.numericKey]) : ''}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      </ScrollView>

      {/* <Text style={tw`text-gray-300 text-xs mt-3`}>
        Datos demo · concentrado anual · canceladas conservan folio · pendiente de backend · communityId: {communityId ?? '—'}
      </Text> */}
    </View>
  );
}
